import { BlogPost } from '@/types/blog'
import { Button } from '@/components/ui/button'
import { MessageCircle, Zap } from 'lucide-react'

const whatsappUrl =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20tenho%20um%20filho%20com%20muita%20energia%20e%20gostaria%20de%20saber%20sobre%20as%20aulas%20de%20futebol%21'

export const postCriancaEnergia: BlogPost = {
  slug: 'atividade-fisica-crianca-energia',
  title:
    'Atividade Física para Criança com Muita Energia - Por que Futebol Funciona',
  excerpt:
    'Seu filho não para quieto? Entenda como o futebol ajuda a canalizar o excesso de energia de forma positiva, melhorando o foco, o sono e o comportamento em casa.',
  date: '16 Fev, 2026',
  author: 'Equipe SuperFut',
  category: 'Comportamento',
  location: 'São Paulo, SP',
  image:
    'https://img.usecurling.com/p/800/400?q=energetic%20kid%20running%20soccer',
  metaDescription:
    'Seu filho tem energia de sobra? Saiba como o futebol canaliza energia de forma saudável e divertida.',
  faq: [
    {
      question: 'O futebol vai deixar ele mais agitado?',
      answer:
        'Pelo contrário. Ao gastar a energia acumulada de forma direcionada, a tendência é que a criança fique mais calma e relaxada após a aula.',
    },
    {
      question: 'Quantas horas por dia ele precisa gastar?',
      answer:
        'Crianças com alta energia podem precisar de mais de 1 hora de atividade vigorosa. O futebol é excelente por ser de alta intensidade.',
    },
    {
      question: 'Ajuda na concentração?',
      answer:
        'Sim. O exercício libera neurotransmissores como dopamina e serotonina, que são essenciais para o foco e regulação do humor.',
    },
    {
      question: 'Ele não para quieto na aula, e agora?',
      answer:
        'Nossos professores estão acostumados! Usamos essa energia a favor da aula, com atividades dinâmicas e pouco tempo de espera em filas.',
    },
    {
      question: 'É sinal de hiperatividade?',
      answer:
        'Ter muita energia é normal na infância. O diagnóstico de TDAH deve ser feito por médico, mas o esporte é benéfico em ambos os casos.',
    },
    {
      question: 'Melhora o comportamento na escola?',
      answer:
        'Frequentemente sim. A "inquietude" na sala de aula muitas vezes é apenas necessidade fisiológica de movimento.',
    },
    {
      question: 'Pode fazer aula todo dia?',
      answer:
        'Pode, desde que respeite o descanso. Variar estímulos é bom, mas a rotina diária de movimento é super recomendada.',
    },
    {
      question: 'Qual o melhor horário?',
      answer:
        'Depende da criança, mas muitos pais preferem final da tarde para "desacelerar" antes da rotina noturna.',
    },
    {
      question: 'O futebol cansa mais que natação?',
      answer:
        'O futebol tem característica intermitente (corre, para, corre), o que é excelente para o condicionamento e gasto calórico.',
    },
    {
      question: 'Vocês aceitam crianças "difíceis"?',
      answer:
        'Com certeza. Não rotulamos crianças. Entendemos a energia como potência de vida que precisa ser direcionada.',
    },
  ],
  content: (
    <div className="space-y-8 text-slate-700 leading-relaxed">
      <div className="prose prose-slate max-w-none">
        <p className="text-xl">
          Seu filho corre pela casa, pula no sofá, sobe nas cadeiras e parece
          ter uma bateria inesgotável? Calma, você não está sozinho. Ter uma
          criança com "excesso" de energia é uma das queixas mais comuns dos
          pais modernos, especialmente em apartamentos. Mas a boa notícia é:
          essa energia não é um problema, é uma potência!
        </p>
      </div>

      <section>
        <h2 className="text-2xl font-bold text-super-text mb-4">
          A Necessidade Fisiológica do Movimento
        </h2>
        <p className="mb-4">
          O corpo da criança foi desenhado para o movimento. Evolutivamente,
          somos nômades. Pedir para uma criança cheia de vitalidade ficar
          sentada horas na escola ou na frente da TV é lutar contra a biologia
          dela.
        </p>
        <p>
          Quando essa energia não é gasta, ela se transforma em ansiedade,
          irritabilidade, dificuldade para dormir e "mau comportamento". A
          criança precisa "esvaziar o tanque" para conseguir relaxar e focar.
        </p>
      </section>

      <section className="bg-orange-50 p-6 rounded-2xl border border-orange-100">
        <div className="flex items-start gap-4">
          <Zap className="w-10 h-10 text-orange-500 flex-shrink-0 mt-1" />
          <div>
            <h2 className="text-2xl font-bold text-super-text mb-2">
              Por que o Futebol é Perfeito?
            </h2>
            <p className="mb-4">
              Diferente de atividades mais estáticas, o futebol é um esporte de{' '}
              <strong>alta intensidade e variedade</strong>.
            </p>
            <ul className="grid gap-2 text-sm sm:grid-cols-2">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full" />
                Explosão muscular (sprints)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full" />
                Resistência aeróbica
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full" />
                Desafio cognitivo constante
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full" />
                Socialização intensa
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-super-text mb-4">
          Dicas de Rotina para "Pilhas Alcalinas"
        </h2>
        <div className="space-y-4">
          <div className="border-l-4 border-super-blue pl-4 py-2 bg-slate-50">
            <h3 className="font-bold text-super-text">
              1. Horário Estratégico
            </h3>
            <p>
              Agende as aulas de futebol para o final da tarde. Isso ajuda a
              gastar a energia acumulada do dia e prepara o corpo para o ciclo
              do sono.
            </p>
          </div>
          <div className="border-l-4 border-super-blue pl-4 py-2 bg-slate-50">
            <h3 className="font-bold text-super-text">2. Reduza o Açúcar</h3>
            <p>
              Alimentação rica em açúcar pode causar picos de energia. Prefira
              frutas e alimentos integrais antes do treino.
            </p>
          </div>
          <div className="border-l-4 border-super-blue pl-4 py-2 bg-slate-50">
            <h3 className="font-bold text-super-text">3. Consistência</h3>
            <p>
              A criança precisa saber que "hoje é dia de futebol". A
              previsibilidade da rotina acalma a ansiedade.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-orange-400 to-red-500 text-white p-8 rounded-3xl text-center shadow-lg">
        <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
          Vamos gastar essa energia juntos?
        </h2>
        <p className="text-lg text-orange-100 mb-8 max-w-2xl mx-auto">
          Traga seu filho para uma aula experimental. Garantimos que ele vai
          voltar para casa suado, feliz e pronto para uma boa noite de sono!
        </p>
        <Button
          size="lg"
          className="bg-white text-orange-600 hover:bg-slate-100 font-bold text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
          asChild
        >
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-6 h-6 mr-2" />
            Canalizar Energia Agora
          </a>
        </Button>
      </section>
    </div>
  ),
}
